import { useEffect, useRef, useState } from 'react';
import {
    fetchLatestWatchlistRevisions as defaultDiscoverLatestWatchlistRevisions,
    fetchWatchlistUpdateOperationStatus as defaultFetchParentWatchlistStatus,
} from '../../utils/ApiCall';
import {
    DEFAULT_ACTIVE_WINDOW_MS,
    OVERALL_STATUS_SUCCESS,
    securityOperationSettleWindowMs,
    DEFAULT_STATUS_RETRY_MAX_MS,
    SECURITY_OPERATION_READ_STATES,
    computeStatusRetryDelay,
    deriveSecurityOperationOverallStatus,
    isSecurityOperationActive,
    isSecurityOperationTerminal,
} from '../../utils/securityOperationStatus';
import {
    canonicalWatchlistPublicationObservation,
    classifyWatchlistPublicationError,
    isTerminalWatchlistRevisionProgress,
    parseWatchlistDeltaClassification,
    requiresWatchlistClientPublication,
    WATCHLIST_CLIENT_PUBLICATION_REQUIRED_FIELD,
    WATCHLIST_DELTA_CLASSIFICATIONS,
    WATCHLIST_REVISION_PROGRESS_EVENT,
    isSecurityOperationEventForScope,
    normalizeSecurityOperationSessionScope,
    scopeSecurityOperationEventDetail,
} from '../../utils/securityResolutionEvents';
import { parseWatchlistPublicationReceipt } from '../../utils/watchlistPublication';
import {
    normalizeWatchlistLifecycle,
    watchlistLifecycleProgressStatus,
} from '../../utils/watchlistLifecycle';

export const DEFAULT_ACTIVE_POLL_INTERVAL_MS = 2000;
export const DEFAULT_REMOVAL_REVIEW_POLL_INTERVAL_MS = 30_000;
export const STATUS_FAILURES_BEFORE_WARNING = 3;
const WATCHLIST_TERMINAL_NON_SUCCESS_STATUSES = new Set([
    'failed',
    'cancelled',
    'stale',
    'superseded',
]);

const defaultFetchWatchlistRevisionStatus = defaultFetchParentWatchlistStatus;

function watchlistStatusReadError(error, fallbackMessage) {
    return classifyWatchlistPublicationError(error, fallbackMessage).message;
}

function boundedPercent(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    return Math.max(0, Math.min(100, numeric));
}

function isWatchlistRemovalReviewPaused(lifecycle) {
    return lifecycle?.status === 'running' && lifecycle?.phase === 'removal_review';
}

export function stagedWatchlistProgressStatus(payload) {
    if (!payload || typeof payload !== 'object') return null;
    return watchlistLifecycleProgressStatus(payload);
}

export default function useSecurityOperationStatus({
    userUid,
    sessionScopeToken,
    enabled = true,
    activePollIntervalMs = DEFAULT_ACTIVE_POLL_INTERVAL_MS,
    removalReviewPollIntervalMs = DEFAULT_REMOVAL_REVIEW_POLL_INTERVAL_MS,
    errorPollIntervalMs,
    errorPollMaxIntervalMs = DEFAULT_STATUS_RETRY_MAX_MS,
    retryJitter = Math.random,
    fetchWatchlistRevisionStatus = defaultFetchWatchlistRevisionStatus,
    discoverLatestWatchlistRevisions = defaultDiscoverLatestWatchlistRevisions,
    now = Date.now,
} = {}) {
    const normalizedSessionScopeToken = normalizeSecurityOperationSessionScope(
        sessionScopeToken,
    );
    const scopeEnabled = Boolean(enabled && userUid && normalizedSessionScopeToken);
    const recoveryScopeKey = scopeEnabled
        ? `${String(userUid).trim()}\u0000${normalizedSessionScopeToken}`
        : '';
    const [statusDoc, setStatusDoc] = useState(null);
    const [readState, setReadState] = useState(
        scopeEnabled
            ? SECURITY_OPERATION_READ_STATES.LOADING
            : SECURITY_OPERATION_READ_STATES.INACTIVE,
    );
    const [readError, setReadError] = useState(null);
    const [publicationObservation, setPublicationObservation] = useState(null);
    const statusRef = useRef(null);
    const stagedClearTimerRef = useRef(null);
    const stagedPollTimerRef = useRef(null);
    const durableRecoveryTimerRef = useRef(null);
    const stagedPollTokenRef = useRef(0);
    const stagedRevisionRef = useRef(null);
    const latestRevisionSequenceRef = useRef({ sequence: 0, revisionId: null });
    const terminalWatermarkRef = useRef(null);
    const statusSampleVersionRef = useRef(0);
    // Revisions whose opening browser event required a client publication
    // without a membership change (a confirmed vendor review). Status samples
    // never repeat that, so the terminal sample carries it on from here.
    const clientPublicationRevisionIdsRef = useRef(new Set());

    useEffect(() => {
        statusRef.current = statusDoc;
    }, [statusDoc]);

    useEffect(() => {
        statusRef.current = null;
        setStatusDoc(null);
        setReadState(
            scopeEnabled
                ? SECURITY_OPERATION_READ_STATES.LOADING
                : SECURITY_OPERATION_READ_STATES.INACTIVE,
        );
        setReadError(null);
        setPublicationObservation(null);
        stagedRevisionRef.current = null;
        latestRevisionSequenceRef.current = { sequence: 0, revisionId: null };
        terminalWatermarkRef.current = null;
        statusSampleVersionRef.current = 0;
        clientPublicationRevisionIdsRef.current = new Set();
        stagedPollTokenRef.current += 1;
        if (stagedPollTimerRef.current) {
            window.clearTimeout(stagedPollTimerRef.current);
            stagedPollTimerRef.current = null;
        }
        if (durableRecoveryTimerRef.current) {
            window.clearTimeout(durableRecoveryTimerRef.current);
            durableRecoveryTimerRef.current = null;
        }
        if (stagedClearTimerRef.current) {
            window.clearTimeout(stagedClearTimerRef.current);
            stagedClearTimerRef.current = null;
        }
    }, [discoverLatestWatchlistRevisions, recoveryScopeKey, scopeEnabled]);

    useEffect(() => {
        if (typeof window === 'undefined' || !scopeEnabled) return undefined;
        let disposed = false;
        let pendingStagedPoll = null;
        let pendingDurableRecovery = null;

        const clearStagedPollTimer = () => {
            if (stagedPollTimerRef.current) {
                window.clearTimeout(stagedPollTimerRef.current);
                stagedPollTimerRef.current = null;
            }
        };

        const clearDurableRecoveryTimer = () => {
            if (durableRecoveryTimerRef.current) {
                window.clearTimeout(durableRecoveryTimerRef.current);
                durableRecoveryTimerRef.current = null;
            }
        };

        const publishRevisionObservation = (payload, revisionId) => {
            const clientPublicationRequired = clientPublicationRevisionIdsRef
                .current.has(revisionId);
            if (
                parseWatchlistDeltaClassification(payload).classification
                !== WATCHLIST_DELTA_CLASSIFICATIONS.MEMBERSHIP_CHANGED
                && !clientPublicationRequired
            ) return;
            const observation = canonicalWatchlistPublicationObservation(payload);
            setPublicationObservation(scopeSecurityOperationEventDetail({
                ...observation,
                watchlist_revision_id: revisionId,
                ...(clientPublicationRequired
                    ? { [WATCHLIST_CLIENT_PUBLICATION_REQUIRED_FIELD]: true }
                    : {}),
                ui_commit_complete: false,
            }, normalizedSessionScopeToken));
            // Keep the accepted obligation through terminal observations and
            // the exact UI commit; later server polls must not lose it.
        };

        const renderRevisionProgress = (rawPayload, {
            recovered = false,
            observationAlreadyPublished = false,
            browserEvent = false,
        } = {}) => {
            let payload;
            try {
                payload = normalizeWatchlistLifecycle(rawPayload);
            } catch (error) {
                setReadState(SECURITY_OPERATION_READ_STATES.UNAVAILABLE);
                setReadError(watchlistStatusReadError(
                    error,
                    'Watchlist revision status is unavailable.',
                ));
                return null;
            }
            // A staged review has no parent operation. Ignore it before
            // touching operation-progress watermarks so a newer draft cannot
            // hide the accepted revision that the browser is still publishing.
            if (payload.status === 'pending_review') return null;
            const revisionId = payload.watchlist_revision_id;
            const publicationReceipt = parseWatchlistPublicationReceipt({
                watchlist_revision_id: revisionId,
                revision_seq: payload.revision_seq,
            });
            const revisionSequence = publicationReceipt?.revisionSeq ?? null;
            const latestRevision = latestRevisionSequenceRef.current;
            if (
                !publicationReceipt
                && (
                    isTerminalWatchlistRevisionProgress(payload)
                    || latestRevision.sequence > 0
                )
            ) {
                return null;
            }
            if (publicationReceipt) {
                if (
                    revisionSequence < latestRevision.sequence
                    || (
                        revisionSequence === latestRevision.sequence
                        && latestRevision.revisionId
                        && revisionId !== latestRevision.revisionId
                    )
                ) {
                    return null;
                }
                if (revisionSequence > latestRevision.sequence) {
                    latestRevisionSequenceRef.current = {
                        sequence: revisionSequence,
                        revisionId,
                    };
                    terminalWatermarkRef.current = null;
                    for (const knownRevisionId of clientPublicationRevisionIdsRef.current) {
                        if (knownRevisionId !== revisionId) clientPublicationRevisionIdsRef.current.delete(knownRevisionId);
                    }
                }
            }
            if (browserEvent && requiresWatchlistClientPublication(rawPayload)) {
                clientPublicationRevisionIdsRef.current.add(revisionId);
            }
            const applied = payload.status === 'succeeded';
            const classificationResult = applied
                ? parseWatchlistDeltaClassification(payload.terminal_receipt)
                : { classification: null, error: null };
            const membershipChanged = (
                classificationResult.classification
                === WATCHLIST_DELTA_CLASSIFICATIONS.MEMBERSHIP_CHANGED
            );
            const clientPublicationRequired = membershipChanged
                || clientPublicationRevisionIdsRef.current.has(revisionId);
            const exactTerminalScope = (candidate) => Boolean(
                candidate
                && candidate.recoveryScopeKey === recoveryScopeKey
                && candidate.revisionId === revisionId
                && candidate.revisionSeq === revisionSequence
            );
            const terminalNonSuccess = WATCHLIST_TERMINAL_NON_SUCCESS_STATUSES.has(
                payload.status,
            );
            let terminalWatermark = terminalWatermarkRef.current;
            if (terminalNonSuccess && publicationReceipt) {
                terminalWatermark = {
                    recoveryScopeKey,
                    revisionId,
                    revisionSeq: revisionSequence,
                    outcome: 'non_success',
                };
                terminalWatermarkRef.current = terminalWatermark;
            } else if (
                exactTerminalScope(terminalWatermark)
                && terminalWatermark.outcome === 'non_success'
            ) {
                return null;
            } else if (
                browserEvent
                && applied
                && clientPublicationRequired
                && rawPayload?.ui_commit_complete === true
                && publicationReceipt
                && !exactTerminalScope(terminalWatermark)
            ) {
                const committedAtMs = Number(now());
                if (Number.isFinite(committedAtMs)) {
                    terminalWatermark = {
                        recoveryScopeKey,
                        revisionId,
                        revisionSeq: revisionSequence,
                        outcome: 'ui_commit',
                        committedAtMs,
                    };
                    terminalWatermarkRef.current = terminalWatermark;
                }
            }
            const exactUiCommit = Boolean(
                exactTerminalScope(terminalWatermark)
                && terminalWatermark.outcome === 'ui_commit'
            );
            if (exactUiCommit && (!applied || !clientPublicationRequired)) {
                return null;
            }
            if (
                exactUiCommit
                && Number(now()) >= terminalWatermark.committedAtMs + securityOperationSettleWindowMs(OVERALL_STATUS_SUCCESS)
            ) {
                if (statusRef.current?.revisionId === revisionId) {
                    if (stagedClearTimerRef.current) {
                        window.clearTimeout(stagedClearTimerRef.current);
                        stagedClearTimerRef.current = null;
                    }
                    stagedRevisionRef.current = null;
                    statusRef.current = null;
                    setStatusDoc(null);
                }
                return null;
            }
            // UI publication completion is an event-envelope concern, not a
            // lifecycle field. The strict DTO intentionally strips it. Keep
            // the exact browser commit latched so a later durable success
            // sample cannot move this same revision back from 100 to 99.
            const uiCommitComplete = applied && clientPublicationRequired && exactUiCommit;
            const next = stagedWatchlistProgressStatus(payload);
            if (!next) return null;
            statusSampleVersionRef.current += 1;
            next.statusSampleVersion = statusSampleVersionRef.current;
            next.revisionSeq = revisionSequence;
            next.uiCommitComplete = uiCommitComplete;
            if (applied) next.deltaClassification = classificationResult.classification;
            if (applied && clientPublicationRequired && !uiCommitComplete) {
                next.topLevelStatus = 'running';
                next.updatedAt = new Date(now());
                next.phase = 'publishing';
                next.message = 'Your Watchlist update is finishing.';
                next.messageCode = 'watchlist_update_publishing';
                next.progressPercent = 99;
                next.phases.factor_matrix_refresh = { status: 'running' };
                next.phases.surface_refresh = { status: 'queued' };
            } else if (applied && clientPublicationRequired && uiCommitComplete) {
                // The visible completion window starts when the browser commits
                // the terminal handoff, not when the server first recorded it.
                next.updatedAt = new Date(terminalWatermark.committedAtMs);
            } else if (!isTerminalWatchlistRevisionProgress(payload)) {
                // Banner freshness measures this accepted observation, not the
                // operation's age. Fresh polls must keep long uploads visible;
                // the watchdog below still recovers work whose polling stops.
                next.updatedAt = new Date(now());
            }
            const previous = statusRef.current;
            if (
                previous?.revisionId === next.revisionId
                && !WATCHLIST_TERMINAL_NON_SUCCESS_STATUSES.has(payload.status)
                && Number.isFinite(Number(previous.progressPercent))
                && Number.isFinite(Number(next.progressPercent))
            ) {
                // Upload acceptance replaces the preview response with the
                // durable parent-operation response. Those two payloads can
                // briefly overlap, and the first parent sample can be 0 even
                // after the browser already painted real staging progress.
                // A revision owns one user-facing lifecycle, so never render
                // a lower percentage for the same revision.
                next.progressPercent = Math.max(
                    boundedPercent(previous.progressPercent),
                    boundedPercent(next.progressPercent),
                );
            }
            // A requirement learned after durable100 must override the
            // same-revision monotonic display until its exact browser commit.
            if (applied && clientPublicationRequired && !uiCommitComplete) {
                next.progressPercent = Math.min(99, next.progressPercent);
            }
            stagedRevisionRef.current = next.operationId;
            setReadState(SECURITY_OPERATION_READ_STATES.FOUND);
            setReadError(null);
            statusRef.current = next;
            setStatusDoc(next);
            if (stagedClearTimerRef.current) {
                window.clearTimeout(stagedClearTimerRef.current);
                stagedClearTimerRef.current = null;
            }
            const overall = deriveSecurityOperationOverallStatus(
                next.phases,
                next.topLevelStatus,
            );
            // A durable server success is intentionally projected as 99% until
            // the browser publishes the replacement snapshot.  Treat that
            // projection as active too; otherwise an older server completion
            // timestamp can clear the shared status before the UI handoff.
            const awaitingUiCommit = applied && clientPublicationRequired && !uiCommitComplete;
            const terminal = !awaitingUiCommit && (
                isTerminalWatchlistRevisionProgress(payload)
                || isSecurityOperationTerminal(overall)
            );
            const settleWindowMs = securityOperationSettleWindowMs(overall);
            const completedAtMs = next.updatedAt instanceof Date
                ? next.updatedAt.getTime()
                : Number.NaN;
            const terminalAgeMs = Number(now()) - completedAtMs;
            const clearDelay = terminal && Number.isFinite(terminalAgeMs)
                ? Math.max(
                    0,
                    settleWindowMs - Math.max(0, terminalAgeMs),
                )
                : terminal ? settleWindowMs : DEFAULT_ACTIVE_WINDOW_MS;
            // Each PostgreSQL progress event resets this watchdog. A recovered
            // terminal keeps only the remainder of its bounded visibility
            // window; nonterminal work retains the longer crash watchdog.
            stagedClearTimerRef.current = window.setTimeout(() => {
                if (stagedRevisionRef.current !== next.operationId) return;
                pendingStagedPoll = null;
                stagedPollTokenRef.current += 1;
                clearStagedPollTimer();
                stagedRevisionRef.current = null;
                statusRef.current = null;
                setStatusDoc(null);
                if (!terminal) {
                    scheduleDurableRecovery(0);
                }
            }, clearDelay);

            if (
                applied
                && clientPublicationRequired
                && recovered
                && !observationAlreadyPublished
                && revisionId
            ) {
                // Recovery consumers need the durable terminal status, not
                // the temporary publishing/99 projection rendered above.
                publishRevisionObservation(payload, revisionId);
            }
            if (terminal) {
                pendingStagedPoll = null;
                stagedPollTokenRef.current += 1;
                clearStagedPollTimer();
            }
            return next;
        };

        const scheduleStagedPoll = (
            revisionId,
            token,
            delay = activePollIntervalMs,
            failureCount = 0,
        ) => {
            clearStagedPollTimer();
            if (disposed || !revisionId || stagedPollTokenRef.current !== token) return;
            pendingStagedPoll = { revisionId, token, failureCount };
            if (
                typeof document !== 'undefined'
                && document.visibilityState === 'hidden'
            ) {
                return;
            }
            stagedPollTimerRef.current = window.setTimeout(async () => {
                stagedPollTimerRef.current = null;
                if (disposed || stagedPollTokenRef.current !== token) return;
                if (
                    typeof document !== 'undefined'
                    && document.visibilityState === 'hidden'
                ) {
                    scheduleStagedPoll(revisionId, token, activePollIntervalMs, failureCount);
                    return;
                }
                try {
                    const result = await fetchWatchlistRevisionStatus({ revisionId });
                    if (disposed || stagedPollTokenRef.current !== token) return;
                    if (!result || typeof result !== 'object' || result.success !== true) {
                        throw new Error(result?.message || 'Watchlist revision status is unavailable.');
                    }
                    {
                        const lifecycle = normalizeWatchlistLifecycle(result);
                        const applied = lifecycle.status === 'succeeded';
                        const terminalNonSuccess = WATCHLIST_TERMINAL_NON_SUCCESS_STATUSES
                            .has(lifecycle.status);
                        const classificationResult = applied
                            ? parseWatchlistDeltaClassification(lifecycle.terminal_receipt)
                            : { classification: null, error: null };
                        if (classificationResult.error) {
                            throw new Error(classificationResult.error);
                        }
                        // The shared header and Watchlist History must render
                        // the same durable parent sample. Publish observations
                        // on a separate event so this hook never consumes and
                        // recursively re-polls its own progress event.
                        if (
                            classificationResult.classification
                            === WATCHLIST_DELTA_CLASSIFICATIONS.MEMBERSHIP_CHANGED
                            || (
                                applied
                                && clientPublicationRevisionIdsRef.current.has(revisionId)
                            )
                        ) {
                            publishRevisionObservation(lifecycle, revisionId);
                        }
                        renderRevisionProgress(lifecycle, {
                            recovered: true,
                            observationAlreadyPublished: true,
                        });
                        const terminal = applied || terminalNonSuccess;
                        if (terminal) {
                            pendingStagedPoll = null;
                            return;
                        }
                        if (isWatchlistRemovalReviewPaused(lifecycle)) {
                            scheduleStagedPoll(
                                revisionId,
                                token,
                                removalReviewPollIntervalMs,
                                0,
                            );
                            return;
                        }
                    }
                    scheduleStagedPoll(revisionId, token, activePollIntervalMs, 0);
                    return;
                } catch (_err) {
                    // PostgreSQL is durable; a transient request failure must
                    // not replace a valid progress sample with a warning. Only
                    // surface the outage after repeated consecutive failures.
                    const nextFailureCount = failureCount + 1;
                    if (
                        !statusRef.current
                        || nextFailureCount >= STATUS_FAILURES_BEFORE_WARNING
                    ) {
                        setReadState(SECURITY_OPERATION_READ_STATES.UNAVAILABLE);
                        setReadError(watchlistStatusReadError(
                            _err,
                            'Watchlist revision status is unavailable.',
                        ));
                    }
                    scheduleStagedPoll(
                        revisionId,
                        token,
                        computeStatusRetryDelay({
                            failureCount: nextFailureCount,
                            baseMs: errorPollIntervalMs,
                            maxMs: errorPollMaxIntervalMs,
                            jitter: retryJitter,
                        }),
                        nextFailureCount,
                    );
                }
            }, delay <= 0 ? 0 : Math.max(500, delay));
        };

        const handleRevisionProgress = (event) => {
            const payload = event?.detail;
            if (!isSecurityOperationEventForScope(
                payload,
                normalizedSessionScopeToken,
            )) return;
            let lifecycle;
            try {
                lifecycle = normalizeWatchlistLifecycle(payload);
            } catch (_error) {
                return;
            }
            const revisionId = lifecycle.watchlist_revision_id;
            const rendered = renderRevisionProgress(payload, { browserEvent: true });
            const terminal = lifecycle.status === 'succeeded'
                || WATCHLIST_TERMINAL_NON_SUCCESS_STATUSES.has(lifecycle.status)
                || lifecycle.status === 'unchanged';
            // A freshly staged upload has a revision id, but its durable parent
            // is created only when the user accepts it. Do not poll the parent
            // endpoint until the progress payload proves processing started.
            // If a terminal payload is missing its strict receipt, keep recovery
            // alive and let the durable endpoint repair the observation.
            if (
                !revisionId
                || terminal
                || !rendered
            ) return;
            const token = stagedPollTokenRef.current + 1;
            stagedPollTokenRef.current = token;
            scheduleStagedPoll(
                revisionId,
                token,
                isWatchlistRemovalReviewPaused(lifecycle)
                    ? removalReviewPollIntervalMs
                    : activePollIntervalMs,
            );
        };
        const handleStagedVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                clearStagedPollTimer();
                clearDurableRecoveryTimer();
                return;
            }
            if (pendingDurableRecovery) {
                scheduleDurableRecovery(0, pendingDurableRecovery.failureCount);
            }
            if (pendingStagedPoll) {
                scheduleStagedPoll(
                    pendingStagedPoll.revisionId,
                    pendingStagedPoll.token,
                    0,
                    pendingStagedPoll.failureCount,
                );
            }
        };

        const scheduleDurableRecovery = (delay, failureCount = 0) => {
            clearDurableRecoveryTimer();
            if (disposed) return;
            pendingDurableRecovery = { failureCount };
            if (
                typeof document !== 'undefined'
                && document.visibilityState === 'hidden'
            ) {
                return;
            }
            durableRecoveryTimerRef.current = window.setTimeout(() => {
                durableRecoveryTimerRef.current = null;
                void recoverDurableWatchlistOperation(failureCount);
            }, delay <= 0 ? 0 : Math.max(500, delay));
        };

        const recoverDurableWatchlistOperation = async (failureCount = 0) => {
            if (typeof discoverLatestWatchlistRevisions !== 'function') {
                pendingDurableRecovery = null;
                return;
            }
            try {
                const latest = await discoverLatestWatchlistRevisions();
                if (disposed) return;
                if (
                    !latest
                    || typeof latest !== 'object'
                    || latest.success === false
                    || !Object.prototype.hasOwnProperty.call(latest, 'current_revision')
                    || !Object.prototype.hasOwnProperty.call(latest, 'pending_revision')
                ) {
                    throw new Error(
                        latest?.message || 'Watchlist revision discovery is unavailable.',
                    );
                }
                const activeRevision = latest?.active_operation_revision;
                const pendingRevision = latest?.pending_revision;
                const recoveryRevision = activeRevision || pendingRevision;
                const revisionId = String(
                    recoveryRevision?.watchlist_revision_id || recoveryRevision?.id || '',
                ).trim();
                const pendingLifecycle = recoveryRevision?.lifecycle
                    ? normalizeWatchlistLifecycle(recoveryRevision.lifecycle)
                    : null;
                let rendered = false;
                if (
                    revisionId
                    && pendingLifecycle
                    && ['queued', 'running'].includes(pendingLifecycle.status)
                ) {
                    rendered = Boolean(renderRevisionProgress(
                        pendingLifecycle,
                        { recovered: true },
                    ));
                    const token = stagedPollTokenRef.current + 1;
                    stagedPollTokenRef.current = token;
                    scheduleStagedPoll(
                        revisionId,
                        token,
                        isWatchlistRemovalReviewPaused(pendingLifecycle)
                            ? removalReviewPollIntervalMs
                            : 0,
                    );
                }
                if (!rendered) {
                    statusRef.current = null;
                    setStatusDoc(null);
                    setReadState(SECURITY_OPERATION_READ_STATES.NOT_FOUND);
                }
                pendingDurableRecovery = null;
                setReadError(null);
            } catch (error) {
                if (disposed) return;
                const nextFailureCount = failureCount + 1;
                setReadState(SECURITY_OPERATION_READ_STATES.UNAVAILABLE);
                setReadError(watchlistStatusReadError(
                    error,
                    'Watchlist revision discovery is unavailable.',
                ));
                scheduleDurableRecovery(computeStatusRetryDelay({
                    failureCount: nextFailureCount,
                    baseMs: errorPollIntervalMs,
                    maxMs: errorPollMaxIntervalMs,
                    jitter: retryJitter,
                }), nextFailureCount);
            }
        };
        window.addEventListener(WATCHLIST_REVISION_PROGRESS_EVENT, handleRevisionProgress);
        document.addEventListener('visibilitychange', handleStagedVisibilityChange);
        scheduleDurableRecovery(0);
        return () => {
            disposed = true;
            window.removeEventListener(WATCHLIST_REVISION_PROGRESS_EVENT, handleRevisionProgress);
            document.removeEventListener('visibilitychange', handleStagedVisibilityChange);
            stagedPollTokenRef.current += 1;
            pendingStagedPoll = null;
            pendingDurableRecovery = null;
            clearStagedPollTimer();
            clearDurableRecoveryTimer();
            if (stagedClearTimerRef.current) {
                window.clearTimeout(stagedClearTimerRef.current);
                stagedClearTimerRef.current = null;
            }
        };
    }, [
        activePollIntervalMs,
        enabled,
        errorPollIntervalMs,
        errorPollMaxIntervalMs,
        discoverLatestWatchlistRevisions,
        fetchWatchlistRevisionStatus,
        normalizedSessionScopeToken,
        now,
        removalReviewPollIntervalMs,
        recoveryScopeKey,
        retryJitter,
        scopeEnabled,
        userUid,
    ]);

    const overallStatus = statusDoc
        ? deriveSecurityOperationOverallStatus(statusDoc.phases, statusDoc.topLevelStatus)
        : null;
    const active = isSecurityOperationActive(statusDoc);

    return {
        statusDoc,
        overallStatus,
        active,
        readState,
        readError,
        publicationObservation,
    };
}
